import type { FilterNode } from '@ofmos/shared/filter-ast';
import {
  GROUP_DEFAULT_LIMIT,
  type RecordGroupTotals,
  ROWS_PER_GROUP_DEFAULT,
} from '@ofmos/shared/record-group';
import type { OrderBy } from '@ofmos/shared/record-query';
import { useMemo } from 'react';
import type { FieldMetadata, ObjectMetadata, RecordGroupEntry } from '../../../data/client';
import { useRecordGroups } from '../../../data/hooks';
import { closedDomainOf, withEmptyGroups } from '../emptyGroups';
import type { ViewState } from '../views/viewState';
import {
  aggregateLabel,
  aggregateLines,
  type GroupAggregatePlan,
  groupAggregatePlan,
  type ResolvedDimension,
  resolveDimension,
} from './groupModel';
import type { TableColumn } from './tableModel';

/**
 * THE GROUPED TABLE'S ONE READ (`docs/decisions/group-by-aggregation.md` D2, D22).
 *
 * The ungrouped table keeps its own path exactly as it was - one `useRecordList` at fifty
 * rows - and this is the other branch: one statement for every group and every aggregate,
 * plus the grand total, under one repeatable-read snapshot. That snapshot is what makes the
 * footer's parts-and-whole claim true rather than aspirational: a header claiming 12 over
 * 11 rows under a footer that agrees with neither is the worst shape a wrong number can
 * have, and every number here comes back from one moment in time.
 *
 * BOTH HOOKS ARE ALWAYS MOUNTED and exactly one is enabled, because a hook cannot be
 * called conditionally and the alternative - two components with two lifetimes - would
 * remount the whole table every time somebody grouped or ungrouped it.
 *
 * EMPTY GROUPS ARE PUT BACK HERE, above the data client and by the same function the board
 * calls (D29): "Won: 0" on a pipeline is the number that motivates the feature, and a
 * `GROUP BY` structurally cannot return it. It happens only where a CLOSED DOMAIN exists to
 * synthesise from, and `withEmptyGroups` refuses over a truncated list of its own accord.
 */
export interface GroupedTable {
  readonly resolved: ResolvedDimension;
  readonly plan: GroupAggregatePlan;
  /**
   * Every field by id, because a renderer's type-dependent decisions - "Earliest" rather
   * than "Smallest", micros rather than a raw integer - are about the field an AGGREGATE
   * names, and the per-group number may name a field nobody pinned as a column.
   */
  readonly fieldById: ReadonlyMap<string, FieldMetadata>;
  /** `undefined` while the read is in flight: the table renders placeholders, not zeroes. */
  readonly groups: readonly RecordGroupEntry[] | undefined;
  readonly total: RecordGroupTotals | undefined;
  /** More groups exist beyond the cap, and NOT how many (D20). */
  readonly hasMoreGroups: boolean;
  readonly groupLimit: number;
  readonly isError: boolean;
  readonly error: Error | null;
  /**
   * HubSpot's in-flight indicator, adopted in spirit (D24): an aggregate being recomputed
   * after a drag says so, rather than showing a stale number that looks correct.
   */
  readonly isFetching: boolean;
}

/**
 * THE PER-GROUP NUMBER AND WHAT IT IS CALLED, or `null` when the header shows the row
 * count - which is D18's default and needs no second label beside "12 records".
 *
 * It lives here rather than beside either renderer because BOTH render it: the desktop
 * heading row and the phone's section heading are the same claim about the same group, and
 * a second copy of this is how the two would start disagreeing about what "Average" is
 * called on a date.
 *
 * The field is read off the whole field list rather than off the visible columns: the
 * per-group number may name a field nobody pinned as a column, which is a legitimate thing
 * to ask for and would otherwise render as a bare figure with no unit.
 */
export function headerAggregate(
  grouped: GroupedTable,
  totals: RecordGroupTotals,
): { readonly label: string; readonly lines: readonly string[] } | null {
  const index = grouped.plan.headerIndex;
  if (index === null) return null;
  const request = grouped.plan.requests[index];
  if (request === undefined) return null;
  const field =
    request.fieldId === undefined ? null : (grouped.fieldById.get(request.fieldId) ?? null);
  return {
    label:
      field === null
        ? aggregateLabel(request.op, null)
        : `${aggregateLabel(request.op, field.type)} of ${field.label.toLowerCase()}`,
    lines: aggregateLines(totals.aggregates[index], request.op, field),
  };
}

export interface UseGroupedTableInput {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly columns: readonly TableColumn[];
  /** The columns actually on screen: only those can carry a footer (D18). */
  readonly shownColumns: readonly TableColumn[];
  readonly state: ViewState;
  /** The filter as the SERVER sees it, with relative-date tokens already resolved. */
  readonly filter: FilterNode | undefined;
  readonly orderBy: readonly OrderBy[] | undefined;
  readonly archived: boolean;
}

export function useGroupedTable({
  object,
  fields,
  columns,
  shownColumns,
  state,
  filter,
  orderBy,
  archived,
}: UseGroupedTableInput): GroupedTable | null {
  const resolved = useMemo(() => resolveDimension(state.groupBy, fields), [state.groupBy, fields]);

  const fieldById = useMemo(() => new Map(fields.map((field) => [field.id, field])), [fields]);

  const plan = useMemo(
    () => groupAggregatePlan(state.groupAggregate, state.columnAggregates, shownColumns),
    [state.groupAggregate, state.columnAggregates, shownColumns],
  );

  /**
   * Every field is selected, not just the visible ones, exactly as the ungrouped list does
   * it: hiding a column must not change the cache key and refetch the page (INV-13), and
   * the record page a row links to reads the same canonical entries.
   */
  const select = useMemo(() => columns.map((column) => column.field.id), [columns]);
  const groupBy = useMemo(() => (resolved === null ? [] : [resolved.dimension]), [resolved]);

  const result = useRecordGroups(object.id, {
    select,
    ...(filter === undefined ? {} : { filter }),
    ...(orderBy === undefined ? {} : { orderBy }),
    groupBy,
    ...(plan.requests.length === 0 ? {} : { aggregates: plan.requests }),
    groupLimit: GROUP_DEFAULT_LIMIT,
    // A PREVIEW SIZE, NOT A FIRST PAGE (D19): expanding a group discards these rows.
    rowsPerGroup: ROWS_PER_GROUP_DEFAULT,
    archivedOnly: archived,
    enabled: resolved !== null,
  });

  const groups = useMemo(() => {
    if (result.data === undefined || resolved === null) return undefined;
    if (state.hideEmptyGroups) return result.data.groups;
    const domain = closedDomainOf(resolved.field);
    return domain === null
      ? result.data.groups
      : withEmptyGroups(result.data, domain, plan.requests);
  }, [result.data, resolved, state.hideEmptyGroups, plan.requests]);

  if (resolved === null) return null;

  return {
    resolved,
    plan,
    fieldById,
    groups,
    total: result.data?.total,
    hasMoreGroups: result.data?.hasMoreGroups ?? false,
    groupLimit: GROUP_DEFAULT_LIMIT,
    isError: result.isError,
    error: result.error,
    isFetching: result.isFetching,
  };
}
